import Cookies from "universal-cookie";
import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {defaultPage, defaultPageSize} from "../../constants/pagination";
import {Timeout} from "react-number-format/types/types";
import {useFetchExpensesQuery} from "../../store/apis/expenseApi";
import {Alert, CircularProgress, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {LoadingItem} from "../loading-item/LoadingItem";
import {getCookieTime} from "../../utils/cookie.utils";
import ExpenseList from "../expense-list/ExpenseList";
import {useFetchUserConfigQuery} from "../../store";
import {endOfMonth, startOfMonth, subMonths} from "date-fns";
import {formatDateTime} from "../../utils/date.utils";
import Box from "@mui/material/Box";
import {skipToken} from "@reduxjs/toolkit/query";

export default function ExpenseWidget() {
    const cookies = new Cookies();
    const {t} = useTranslation();
    const today = new Date();
    const [alignment, setAlignment] = React.useState('current');
    const [dateFrom, setDateFrom] = useState(formatDateTime(startOfMonth(today)));
    const [dateTo, setDateTo] = useState(formatDateTime(endOfMonth(today)));
    const [page, setPage] = useState(defaultPage);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [query, setQuery] = useState('');
    const [loadTimeoutId, setLoadTimeoutId] = useState<Timeout | undefined>(undefined)
    const {
        data: userConfig,
        error: userConfigError,
        isFetching: isUserConfigFetching,
    } = useFetchUserConfigQuery();

    const {
        data: pageable,
        isLoading: isExpensesLoading,
        error: expensesError,
        refetch: refetchExpenses
    } = useFetchExpensesQuery(
        userConfig ? {
            providerId: userConfig.providerId,
            dateFrom,
            dateTo,
            q: query,
            page,
            pageSize,
        } : skipToken,
        {refetchOnMountOrArgChange: true, skip: !userConfig}
    );

    const handleChangeDates = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string,
    ) => {
        if (newAlignment) {
            setAlignment(newAlignment);

            if (newAlignment === "previous") {
                const prevMonth = subMonths(today, 1);
                setDateFrom(formatDateTime(startOfMonth(prevMonth)));
                setDateTo(formatDateTime(endOfMonth(prevMonth)));
            } else if (newAlignment === "current") {
                setDateFrom(formatDateTime(startOfMonth(today)));
                setDateTo(formatDateTime(endOfMonth(today)));
            }

            delayLoadExpenses();
        }
    };

    const delayLoadExpenses = () => {
        if (typeof loadTimeoutId === 'number') {
            clearTimeout(loadTimeoutId);
            setLoadTimeoutId(undefined);
        }

        const newId = setTimeout(() => {
            refetchExpenses().then();
        }, 1500);
        setLoadTimeoutId(newId);
    };

    const onChangeQuery = (event: any) => {
        let cleanedValue = event.target.value.replace(/[^\w\s.,]/g, "");
        cleanedValue = cleanedValue.replace(",", ".");
        setQuery(cleanedValue);
        delayLoadExpenses();
    };

    const onChangePage = async (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    }

    const onChangePageSize = async (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPage(0);
        setPageSize(newPageSize);
        const expires = getCookieTime();
        cookies.set('pageSize', newPageSize, {expires});
    }

    if (isExpensesLoading) {
        return <LoadingItem><CircularProgress/></LoadingItem>;
    }

    if (expensesError) {
        return <Alert severity="error">{t("Cannot load planned expenses!")}</Alert>;
    }

    if (!pageable && !isExpensesLoading) {
        return <Alert severity="info">No data!</Alert>;
    }

    return (
        <div>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: {xs: "center", sm: "flex-start"}
                }}
            >
                <ToggleButtonGroup
                    color="primary"
                    value={alignment}
                    exclusive
                    onChange={handleChangeDates}
                    aria-label="Platform"
                >
                    <ToggleButton value="previous">Previous month</ToggleButton>
                    <ToggleButton value="current">Current month</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <ExpenseList
                pageable={pageable}
                query={query}
                page={page}
                pageSize={pageSize}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
                loadData={() => {
                    refetchExpenses()
                }}
                onChangeQuery={onChangeQuery}
            />
        </div>
    );
};